// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Loader2 } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

/**
 * AuthCallback — handles the redirect back from auth.emergentagent.com
 *
 * Reads the `session_id` from the URL fragment (#session_id=...), exchanges it
 * with our backend for a regular JWT, persists the token in localStorage and
 * navigates to the home page. Designed to run synchronously and survive React
 * StrictMode double-invocation via a useRef flag.
 */
const AuthCallback = () => {
  const navigate = useNavigate();
  const hasProcessed = useRef(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    const exchange = async () => {
      const hash = window.location.hash || '';
      const match = hash.match(/session_id=([^&]+)/);
      const sessionId = match ? decodeURIComponent(match[1]) : '';

      if (!sessionId) {
        setError('Não recebemos um identificador de sessão. Tente novamente.');
        setTimeout(() => navigate('/auth', { replace: true }), 2000);
        return;
      }

      try {
        const resp = await axios.post(
          `${API}/api/auth/google/session`,
          {},
          {
            headers: { 'X-Session-ID': sessionId },
            withCredentials: true,
            timeout: 15000,
          },
        );
        if (resp.data?.token) {
          localStorage.setItem('tdo_token', resp.data.token);
        }
        // Clear the fragment to avoid re-running this on back/forward navigation
        window.history.replaceState(null, '', window.location.pathname);
        navigate('/', { replace: true, state: { user: resp.data } });
      } catch (err) {
        // eslint-disable-next-line no-console
        console.error('[AuthCallback] failed to exchange session', err?.response?.data || err);
        setError(
          err?.response?.data?.detail ||
          'Não foi possível concluir o login com Google. Tente novamente.'
        );
        setTimeout(() => navigate('/auth', { replace: true }), 2500);
      }
    };

    exchange();
  }, [navigate]);

  return (
    <div
      className="min-h-screen flex items-center justify-center bg-[#F4F6F8] px-4"
      data-testid="auth-callback"
    >
      <div className="text-center max-w-sm">
        <Loader2 className="w-10 h-10 animate-spin text-[#006030] mx-auto mb-4" />
        {error ? (
          <>
            <p className="text-sm text-[#C62828] font-medium" data-testid="auth-callback-error">
              {error}
            </p>
            <p className="text-xs text-[#94A3B8] mt-2">
              Redirecionando para a tela de login…
            </p>
          </>
        ) : (
          <>
            <p className="text-sm text-[#0F1C2E] font-medium">Concluindo seu login com Google…</p>
            <p className="text-xs text-[#94A3B8] mt-1">Isso leva apenas alguns segundos.</p>
          </>
        )}
      </div>
    </div>
  );
};

export default AuthCallback;
